import {
  Activity,
  AlarmClock,
  BellRing,
  BookOpen,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  DatabaseZap,
  FileCheck2,
  FileText,
  Flame,
  Gauge,
  GitBranch,
  GraduationCap,
  Hammer,
  House,
  Info,
  KeyRound,
  Layers,
  LayoutDashboard,
  ListChecks,
  Mail,
  MessageCircleQuestion,
  MessageSquarePlus,
  MessagesSquare,
  MonitorPlay,
  Network,
  Package,
  RefreshCcwDot,
  Scale,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkle,
  Sparkles,
  Timer,
  TrendingUp,
  Truck,
  UserRound,
  UsersRound,
  Waypoints,
  type LucideIcon
} from "lucide-react";
import {
  AdminOnly,
  AuditReaders,
  ComplianceReaders,
  CustodyReaders,
  DocumentApprovers,
  ExecutiveMenu,
  GovernedOperations,
  hasAnyRole,
  isReadOnlyExecutive,
  isPurchaserOnly,
  ManagementReaders,
  MasterScheduleControl,
  OperationsData,
  OperationsPrincipals,
  Participants,
  PerPersonReaders,
  PurchaserMenu,
  SchedulePlanners,
  WallDisplayManagers,
  LogisticsReaders,
  type Role
} from "../../auth/roles";
import { PurchasingWorkspaceReaders, ShippingReaders } from "../../pages/areas/supplyArea";
import { TrainingCertificateReaders, TrainingMatrixReaders } from "../../pages/areas/trainingArea";
import { PeopleScreenReaders } from "../../pages/areas/peopleArea";
import { AnalyticsReaders } from "../../pages/areas/analyticsArea";
import { MyBriefScheduleReaders } from "../../pages/areas/customersHubArea";
import { isFeatureOn } from "../../lib/featureSwitches";

/** Floor Command (D-O76): everyone who may read plant operations (the API policy is OperationsData). */
const FloorCommandReaders: readonly Role[] = OperationsData;

// Tabbed navigation hubs, ported from Summit Engineering (D-0179). Every screen keeps its own URL; the left menu shows
// one link per hub the person can use, and the hub's tab strip sits on top of every page that belongs to it.
//
// The hubs and their pages are Planning/FUNCTIONAL_SPEC.md "Navigation hubs". Each tab's `allow` list is the policy
// that protects its data in docs/contracts/ROLES_AND_POLICIES.md §2 — a page a role cannot read is not on its menu.
// The API refuses it either way; the menu simply agrees.

/** One screen: a tab of its hub. `allow` is the same role list as the route guard in App.tsx. */
export interface NavItem {
  /** Stable id (test ids, tab DOM ids, badges). Unique across every hub. */
  id: string;
  to: string;
  label: string;
  icon: LucideIcon;
  allow: readonly Role[];
  /** Only the exact path is this screen (not its sub-paths). */
  end?: boolean;
  /** Sub-paths that belong to another screen. */
  excludes?: readonly string[];
  /** Extra path prefixes whose pages belong to this tab (e.g. a work order opened from the hub). */
  also?: readonly string[];
  /** The build phase and module that bring this screen to life, when it is not live yet. */
  phase?: string;
  /** Release 1.6.0: shown only while this feature switch is on (lib/featureSwitches; the API is the authority). */
  feature?: string;
  /** Tabs that belong together (2026-10-23 menu reorganisation): divided in the strip, headed in "More". */
  group?: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export type HubId = "my" | "operations" | "planning" | "shop-floor" | "purchasing" | "logistics" | "people" | "analytics" | "knowledge" | "quality" | "viper" | "admin" | "about";

export interface Hub {
  id: HubId;
  label: string;
  icon: LucideIcon;
  /** The hub's own URL, which sends the person to their first permitted tab; null when the first tab is the root. */
  root: string | null;
  /** "footer": the small About area at the foot of the sidebar. */
  placement: "main" | "footer";
  /** A single top-level page with no tab strip. */
  standalone?: boolean;
  items: NavItem[];
}

export const HUBS: readonly Hub[] = [
  {
    // Every person's own page: what is assigned to them, what they asked, their training and their notifications.
    id: "my",
    label: "My work",
    icon: House,
    root: "/my",
    placement: "main",
    items: [
      { id: "my-work", to: "/my/work", label: "My assigned work", icon: House, allow: Participants },
      { id: "my-questions", to: "/my/questions", label: "My questions", icon: MessageCircleQuestion, allow: Participants },
      { id: "my-training", to: "/my/training", label: "My training", icon: GraduationCap, allow: Participants },
      { id: "my-notifications", to: "/my/notifications", label: "My notifications", icon: BellRing, allow: OperationsPrincipals },
      { id: "my-profile", to: "/profile", label: "My profile & certificates", icon: UserRound, allow: OperationsPrincipals },
      { id: "my-feedback", to: "/my/feedback", label: "My feedback", icon: MessageSquarePlus, allow: OperationsPrincipals }
    ]
  },
  {
    // The Operations suite. Its root is the Operations Hub itself (/operations), so the hub link needs no separate URL.
    id: "operations",
    label: "Operations",
    icon: LayoutDashboard,
    root: null,
    placement: "main",
    items: [
      { id: "operations-hub", to: "/operations", label: "Operations Hub", icon: LayoutDashboard, allow: OperationsData, also: ["/work-orders"], group: "Overview" },
      { id: "alerts", to: "/operations/alerts", label: "Alerts & risk", icon: ShieldAlert, allow: OperationsData, group: "Overview" },
      { id: "weekly-report", to: "/operations/weekly-report", label: "Weekly report", icon: ClipboardList, allow: ManagementReaders, group: "Overview" },
      { id: "work-orders", to: "/operations/work-orders", label: "Work orders", icon: ClipboardList, allow: OperationsData, group: "Work orders" },
      { id: "ready-for-operations", to: "/operations/ready", label: "Ready for Operations", icon: ListChecks, allow: OperationsData, group: "Work orders" },
      { id: "schedule", to: "/operations/master-schedule", label: "Master schedule", icon: CalendarRange, allow: OperationsData, group: "Work orders" },
      { id: "trees", to: "/operations/trees", label: "Assembly trees", icon: GitBranch, allow: OperationsData, group: "Work orders" },
      { id: "wip", to: "/operations/wip", label: "WIP", icon: Layers, allow: OperationsData, group: "Work orders" },
      { id: "completed-ops", to: "/operations/completed-ops", label: "Completed operations", icon: ListChecks, allow: OperationsData, group: "Work orders" },
      { id: "out-of-sequence", to: "/operations/out-of-sequence", label: "Out-of-sequence buy-offs", icon: ListChecks, allow: OperationsData, group: "Work orders" },
      { id: "lob", to: "/operations/line-of-balance", label: "Line of Balance", icon: TrendingUp, allow: OperationsData, group: "Programs" },
      { id: "program-lob", to: "/operations/program-lob", label: "Program LOB", icon: TrendingUp, allow: OperationsData, group: "Programs" },
      { id: "customers", to: "/customers", label: "Customers", icon: UsersRound, allow: OperationsData, group: "Customers" },
      { id: "customer-brief", to: "/operations/customers/brief", label: "Customer brief", icon: TrendingUp, allow: OperationsData, group: "Customers" },
      { id: "my-briefs", to: "/customers/my-briefs", label: "My scheduled briefs", icon: Mail, allow: MyBriefScheduleReaders, group: "Customers" }
    ]
  },
  {
    // Finite-capacity planning (D-O74/D-O75): the order and machine Gantts on the deterministic scheduler, and the
    // Scenario lab where drafts are compared, rebalanced, applied and published (signed).
    id: "planning",
    label: "Planning",
    icon: CalendarRange,
    root: "/planning",
    placement: "main",
    items: [
      // Release 1.6.0 (O-R82, O-R81, O-R74, O-R72): hidden until their governed switches are on. Ids must not match an App
      // PAGES key (the old "viper-rebalance" placeholder would take the route); planningArea owns these routes.
      { id: "planning-cockpit", to: "/planning/cockpit", label: "Decisions today", icon: ClipboardCheck, allow: MasterScheduleControl, feature: "planning.cockpit", group: "Today" },
      { id: "planning-gss-updates", to: "/planning/gss-updates", label: "GSS updates", icon: ListChecks, allow: OperationsPrincipals, feature: "planning.cockpit", group: "Today" },
      // Release 1.2.0: the master scheduler's own screen.
      { id: "master-schedule-control", to: "/planning/master", label: "Master control", icon: Scale, allow: MasterScheduleControl, group: "Schedule" },
      { id: "planning-calendar", to: "/planning/calendar", label: "Calendar", icon: CalendarDays, allow: OperationsData, feature: "planning.calendar", group: "Schedule" },
      { id: "order-gantt", to: "/planning/orders", label: "Order Gantt", icon: CalendarRange, allow: OperationsData, group: "Schedule" },
      { id: "resource-gantt", to: "/planning/resources", label: "Machine Gantt", icon: Gauge, allow: OperationsData, group: "Schedule" },
      { id: "scenario-lab", to: "/planning/scenarios", label: "Scenario lab", icon: Sparkles, allow: SchedulePlanners, group: "What-if" },
      { id: "planning-viper-rebalance", to: "/planning/viper-rebalance", label: "VIPER Rebalance", icon: Sparkles, allow: MasterScheduleControl, feature: "planning.viper_rebalance", group: "What-if" }
    ]
  },
  {
    id: "shop-floor",
    label: "Shop floor",
    icon: Hammer,
    root: "/shop-floor",
    placement: "main",
    items: [
      { id: "dispatch", to: "/shop-floor/dispatch", label: "Dispatch lists", icon: ListChecks, allow: OperationsData, group: "Run the floor" },
      { id: "floor-command", to: "/floor-command", label: "Floor Command", icon: MonitorPlay, allow: FloorCommandReaders, group: "Run the floor" },
      { id: "kanban", to: "/shop-floor/kanban", label: "Shop Kanban", icon: Layers, allow: OperationsData, group: "Run the floor" },
      { id: "working-now", to: "/shop-floor/working-now", label: "Who's working", icon: Timer, allow: OperationsData, group: "Run the floor" },
      { id: "sqdc", to: "/shop-floor/sqdc", label: "SQDC board", icon: Activity, allow: OperationsData, group: "Run the floor" },
      { id: "hotlist", to: "/shop-floor/hotlist", label: "Hotlist & holds", icon: Flame, allow: OperationsData, group: "Watch list" },
      { id: "parked", to: "/shop-floor/parked", label: "Parked work orders", icon: AlarmClock, allow: OperationsData, group: "Watch list" },
      { id: "capacity", to: "/shop-floor/capacity", label: "Capacity & machines", icon: Gauge, allow: OperationsData, group: "Watch list" },
      { id: "material", to: "/shop-floor/material", label: "Material & OSP", icon: Package, allow: OperationsData, group: "Material & shipping" },
      { id: "osp-board", to: "/shop-floor/osp", label: "OSP board", icon: Package, allow: OperationsData, group: "Material & shipping" },
      { id: "shipping", to: "/shop-floor/shipping", label: "Shipping", icon: Package, allow: ShippingReaders, group: "Material & shipping" }
    ]
  },
  {
    // The purchasers' own workspace (D-O67): sent outside-processing requests, the digital work-order router, approve and
    // print. Since release 1.4.0 a Purchaser starts here and also sees the work orders, material & OSP and the OSP board.
    id: "purchasing",
    label: "Purchasing",
    icon: ClipboardCheck,
    root: null,
    placement: "main",
    standalone: true,
    items: [
      {
        id: "purchasing-workspace",
        to: "/purchasing",
        label: "Purchasing workspace",
        icon: ClipboardCheck,
        allow: PurchasingWorkspaceReaders,
        also: ["/purchasing/requests"]
      }
    ]
  },
  {
    // Release 1.9.0 (Logistics, D-O151/D-O152): what is coming and going, the Logistics inbox and shipments.
    id: "logistics",
    label: "Logistics",
    icon: Truck,
    root: "/logistics",
    placement: "main",
    items: [
      { id: "logistics-coming-going", to: "/logistics/coming-going", label: "Coming & Going", icon: LayoutDashboard, allow: LogisticsReaders },
      { id: "logistics-inbox", to: "/logistics/inbox", label: "Logistics inbox", icon: Truck, allow: LogisticsReaders, also: ["/logistics/shipments"] }
    ]
  },
  {
    // Per-person data (Q-O13, D-O60): the Operations Manager, the Planner and Admin see every employee; everyone else
    // (Shop Supervisors included) sees only themselves on My work.
    id: "people",
    label: "People & load",
    icon: UsersRound,
    root: "/people",
    placement: "main",
    items: [
      { id: "load-board", to: "/people/load", label: "Load board", icon: UsersRound, allow: PeopleScreenReaders, also: ["/people/person"], group: "Workload" },
      { id: "my-team", to: "/people/team", label: "My team", icon: UsersRound, allow: PeopleScreenReaders, group: "Workload" },
      { id: "assignments", to: "/people/assignments", label: "Assignments", icon: Waypoints, allow: SchedulePlanners, group: "Workload" },
      { id: "machine-operators", to: "/people/machine-operators", label: "Machine operators", icon: Waypoints, allow: PerPersonReaders, group: "Workload" },
      { id: "time-off", to: "/people/time-off", label: "Time off", icon: AlarmClock, allow: PeopleScreenReaders, group: "Workload" },
      { id: "skills", to: "/people/skills", label: "Skills & cross-training", icon: GraduationCap, allow: PeopleScreenReaders, group: "Skills & training" },
      { id: "training-matrix", to: "/training/matrix", label: "Training matrix", icon: GraduationCap, allow: TrainingMatrixReaders, group: "Skills & training" },
      { id: "training-gaps", to: "/training/gaps", label: "Training gaps", icon: GraduationCap, allow: TrainingMatrixReaders, group: "Skills & training" },
      { id: "training-certificates", to: "/training/certificates", label: "Certificates review", icon: GraduationCap, allow: TrainingCertificateReaders, group: "Skills & training" },
      { id: "efficiency", to: "/people/efficiency", label: "Efficiency", icon: TrendingUp, allow: PeopleScreenReaders, group: "Reviews" },
      { id: "team-meeting", to: "/people/team-meeting", label: "Team meeting prep", icon: MessagesSquare, allow: PerPersonReaders, group: "Reviews" },
      { id: "management-review", to: "/people/management-review", label: "Management review", icon: ClipboardList, allow: PerPersonReaders, group: "Reviews" }
    ]
  },
  {
    // In-app BI (D-O53, D-O77): the KPI library, trends, compare, drill-down, anomalies and "Explain this".
    id: "analytics",
    label: "Analytics",
    icon: TrendingUp,
    root: null,
    placement: "main",
    standalone: true,
    items: [{ id: "analytics", to: "/analytics", label: "Analytics", icon: TrendingUp, allow: AnalyticsReaders, also: ["/analytics/kpi"] }]
  },
  {
    id: "quality",
    label: "Quality & compliance",
    icon: ShieldCheck,
    root: "/quality",
    placement: "main",
    items: [
      { id: "as9100", to: "/quality/as9100", label: "AS9100 self-assessment", icon: ClipboardCheck, allow: ComplianceReaders, group: "Self-assessments" },
      { id: "cmmc", to: "/quality/cmmc", label: "CMMC self-assessment", icon: ShieldCheck, allow: ComplianceReaders, group: "Self-assessments" },
      { id: "ssp", to: "/quality/ssp", label: "System security plan", icon: ShieldCheck, allow: ComplianceReaders, group: "Self-assessments" },
      { id: "validation", to: "/quality/validation", label: "System validation", icon: ClipboardCheck, allow: ComplianceReaders, group: "Self-assessments" },
      { id: "approvals", to: "/quality/approvals", label: "Approvals", icon: FileCheck2, allow: DocumentApprovers, group: "Records & approvals" },
      { id: "custody", to: "/quality/custody", label: "Chain of custody", icon: ScrollText, allow: CustodyReaders, group: "Records & approvals" },
      { id: "forms", to: "/quality/forms", label: "Controlled forms", icon: ClipboardList, allow: ComplianceReaders, group: "Records & approvals" },
      { id: "records", to: "/quality/records", label: "Records retention", icon: ScrollText, allow: CustodyReaders, group: "Records & approvals" }
    ]
  },
  {
    id: "knowledge",
    label: "Knowledge",
    icon: BookOpen,
    root: "/knowledge",
    placement: "main",
    items: [
      { id: "knowledge-base", to: "/knowledge/base", label: "Knowledge base", icon: BookOpen, allow: OperationsPrincipals },
      { id: "procedures", to: "/knowledge/procedures", label: "Procedures & work instructions", icon: FileText, allow: OperationsPrincipals },
      { id: "incidents", to: "/knowledge/incidents", label: "Incidents & safety", icon: ShieldAlert, allow: OperationsPrincipals },
      { id: "training", to: "/training", label: "Training", icon: GraduationCap, allow: OperationsPrincipals }
    ]
  },
  {
    id: "viper",
    label: "VIPER",
    icon: MessagesSquare,
    root: null,
    placement: "main",
    items: [
      { id: "viper-ask", to: "/viper", label: "Ask VIPER", icon: MessagesSquare, allow: OperationsData },
      { id: "viper-brief", to: "/viper/brief", label: "Morning brief", icon: Sparkles, allow: OperationsData }
    ]
  },
  {
    id: "admin",
    label: "Admin",
    icon: SlidersHorizontal,
    root: "/admin",
    placement: "main",
    items: [
      { id: "users-roles", to: "/admin/users-roles", label: "Users & roles", icon: KeyRound, allow: GovernedOperations, group: "People & access" },
      // Release 1.5.0 (O-R64): which page each role starts on; the page has its own route in App.tsx.
      { id: "admin-start-pages", to: "/admin/start-pages", label: "Role start pages", icon: House, allow: AdminOnly, group: "People & access" },
      { id: "access-review", to: "/admin/access-review", label: "Access review", icon: ClipboardCheck, allow: GovernedOperations, group: "People & access" },
      { id: "clearance", to: "/admin/clearance", label: "Clearance", icon: ShieldCheck, allow: GovernedOperations, group: "People & access" },
      { id: "training-admin", to: "/admin/training", label: "Training admin", icon: GraduationCap, allow: GovernedOperations, group: "People & access" },
      { id: "data-sync", to: "/admin/data-sync", label: "Data sync", icon: RefreshCcwDot, allow: GovernedOperations, group: "Data & settings" },
      { id: "data-health", to: "/admin/data-health", label: "Data health", icon: Activity, allow: GovernedOperations, group: "Data & settings" },
      { id: "governed", to: "/admin/governed", label: "Governed settings", icon: SlidersHorizontal, allow: GovernedOperations, group: "Data & settings" },
      { id: "audit", to: "/admin/audit", label: "Audit", icon: ScrollText, allow: AuditReaders, group: "Data & settings" },
      // Release 1.9.0 Logistics (LOGISTICS_PLAN.md §6): carriers, accounts (Key Vault secret names only), connection tests.
      { id: "carrier-admin", to: "/admin/carriers", label: "Carriers", icon: Package, allow: AdminOnly, group: "Integrations" },
      { id: "communications", to: "/admin/communications", label: "Communications", icon: Mail, allow: AdminOnly, group: "Messages & displays" },
      { id: "admin-notifications", to: "/admin/notifications", label: "Notifications", icon: BellRing, allow: GovernedOperations, group: "Messages & displays" },
      { id: "wall-displays", to: "/admin/wall-displays", label: "Wall displays", icon: MonitorPlay, allow: WallDisplayManagers, group: "Messages & displays" },
      { id: "admin-releases", to: "/admin/releases", label: "Releases", icon: Sparkle, allow: GovernedOperations, group: "Messages & displays" },
      { id: "ai-activity", to: "/admin/ai-activity", label: "AI activity", icon: Sparkles, allow: AdminOnly, group: "VIPER" },
      { id: "knowledge-index", to: "/admin/knowledge-index", label: "Knowledge index", icon: DatabaseZap, allow: AdminOnly, group: "VIPER" },
      { id: "pilot-hub", to: "/admin/pilot", label: "Pilot hub", icon: MessageSquarePlus, allow: GovernedOperations, group: "Pilot & value" },
      { id: "feedback", to: "/admin/feedback", label: "Feedback", icon: MessageCircleQuestion, allow: GovernedOperations, group: "Pilot & value" },
      { id: "adoption", to: "/admin/adoption", label: "Adoption", icon: Activity, allow: ManagementReaders, group: "Pilot & value" },
      { id: "roi", to: "/admin/roi", label: "ROI", icon: TrendingUp, allow: ManagementReaders, group: "Pilot & value" }
    ]
  },
  {
    id: "about",
    label: "About",
    icon: Info,
    root: "/about",
    placement: "footer",
    items: [
      { id: "release-notes", to: "/about/release-notes", label: "Release notes", icon: Sparkle, allow: OperationsPrincipals },
      { id: "architecture", to: "/about/architecture", label: "Architecture", icon: Network, allow: OperationsPrincipals },
      { id: "ai-ethics", to: "/about/ai-ethics", label: "AI ethics", icon: Scale, allow: OperationsPrincipals },
      { id: "ai-policies", to: "/about/ai-policies", label: "AI policies", icon: UserRound, allow: OperationsPrincipals },
      { id: "pilot-guide", to: "/about/pilot-guide", label: "Pilot guide", icon: BookOpen, allow: OperationsPrincipals, also: ["/pilot/guide"] }
    ]
  }
];

export const HUB_BY_ID: Readonly<Record<HubId, Hub>> = Object.fromEntries(HUBS.map((h) => [h.id, h])) as Record<HubId, Hub>;

/** Every screen, grouped by hub — the registry the tests and the route table read. */
export const NAV: NavSection[] = HUBS.map((h) => ({ heading: h.label, items: h.items }));

/** Every screen in the application, flat and in menu order, whatever the roles. */
export const ALL_ITEMS: readonly NavItem[] = HUBS.flatMap((h) => h.items);

/** May this person open the screen? Its `allow` list — and for the read-only Executive or a Purchaser only their own list. */
export function canSee(item: NavItem, roles: readonly string[]): boolean {
  return (
    hasAnyRole(roles, item.allow) &&
    (!item.feature || isFeatureOn(item.feature)) &&
    (!isReadOnlyExecutive(roles) || ExecutiveMenu.includes(item.to)) &&
    (!isPurchaserOnly(roles) || PurchaserMenu.includes(item.to))
  );
}

/** The screens a person may open, grouped by hub (empty groups dropped). */
export function navFor(roles: readonly string[]): NavSection[] {
  return NAV.map((s) => ({ ...s, items: s.items.filter((i) => canSee(i, roles)) })).filter((s) => s.items.length > 0);
}

/** Every screen a person may open, flat and in menu order. */
export function screensFor(roles: readonly string[]): NavItem[] {
  return navFor(roles).flatMap((s) => s.items);
}

export interface VisibleHub {
  hub: Hub;
  tabs: NavItem[];
  /** Where the menu link goes: the person's first permitted tab. */
  to: string;
}

/** The hubs a person sees (a hub with no permitted tab is hidden), each with only its permitted tabs. */
export function hubsFor(roles: readonly string[]): VisibleHub[] {
  const visible = HUBS.map((hub) => ({ hub, tabs: hub.items.filter((i) => canSee(i, roles)) }))
    .filter((v) => v.tabs.length > 0)
    .map((v) => ({ ...v, to: v.tabs[0]!.to }));
  // Release 1.4.0: a Purchaser starts in their own workspace (the start page is the first hub).
  if (!isPurchaserOnly(roles)) return visible;
  return [...visible.filter((v) => v.hub.id === "purchasing"), ...visible.filter((v) => v.hub.id !== "purchasing")];
}

const under = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

/** How specifically a screen claims a path: the length of its longest matching prefix, or -1. */
function claim(item: NavItem, pathname: string): number {
  if (item.excludes?.some((p) => under(pathname, p))) return -1;
  let best = -1;
  for (const base of [item.to, ...(item.also ?? [])]) {
    const hit = item.end ? pathname === base : under(pathname, base);
    if (hit && base.length > best) best = base.length;
  }
  return best;
}

/** The screen (and its hub) a path belongs to — longest prefix wins, so /viper/brief is the brief, not Ask VIPER. */
export function ownerOf(pathname: string): { hub: Hub; item: NavItem } | null {
  let found: { hub: Hub; item: NavItem } | null = null;
  let score = -1;
  for (const hub of HUBS) {
    for (const item of hub.items) {
      const s = claim(item, pathname);
      if (s > score) {
        score = s;
        found = { hub, item };
      }
    }
  }
  return found;
}

/** Is the hub's menu link the current one? The path belongs to one of its tabs, or is the hub's own root URL. */
export function isHubActive(hub: Hub, pathname: string): boolean {
  if (hub.root && under(pathname, hub.root) && ownerOf(pathname) === null) return true;
  return ownerOf(pathname)?.hub.id === hub.id;
}

export type RedirectTarget = { kind: "hub"; hub: HubId } | { kind: "path"; to: string };

/**
 * Hub roots and alias URLs. A hub root sends the person to their first permitted tab; the query string and hash are
 * kept. Add an entry here when a screen ever moves, so no link breaks.
 */
export const OLD_ROUTE_REDIRECTS: Readonly<Record<string, RedirectTarget>> = {
  ...Object.fromEntries(HUBS.filter((h) => h.root).map((h) => [h.root!, { kind: "hub", hub: h.id } satisfies RedirectTarget])),
  "/hub": { kind: "path", to: "/operations" },
  // Release 1.4.0: the invite link the owner uses (/admin/access?invite=…) opens Users & roles with the query kept.
  "/admin/access": { kind: "path", to: "/admin/users-roles" },
  // Release 1.9.0 W2 (D-O160): the Purchasing view is now the Watch tab of the one Purchasing workspace.
  "/shop-floor/purchasing": { kind: "path", to: "/purchasing?tab=watch" }
};

/** Where an old URL goes for this person, or null when it is not a redirect. */
export function resolveOldRouteRedirect(pathname: string, roles: readonly string[]): string | null {
  const key = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const target = OLD_ROUTE_REDIRECTS[key];
  if (!target) return null;
  if (target.kind === "path") return target.to;
  return hubsFor(roles).find((v) => v.hub.id === target.hub)?.to ?? "/";
}

/** document.title for a tab: "<Tab> \u00b7 <Hub> \u00b7 Summit Operations" (the hub is left out when it has the tab's name). */
export function hubTitle(tab: string | null, hub: string | null): string {
  const parts = [tab, hub && hub !== tab ? hub : null].filter((p): p is string => !!p);
  return [...parts, "Summit Operations"].join(" \u00b7 ");
}


